import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import TaleButton from "@components/TaleButton";
import { PaymentProofHistory } from "@components/PaymentProof/PaymentProofHistory";
import { invoicesService } from "@/api/invoices";
import { useTranslation } from "@/hooks/useTranslation";

const isImageOrPdf = (file) => file.type.startsWith("image/") || file.type === "application/pdf";

const formatDate = (date) => (date ? new Date(date).toLocaleDateString("fr-FR") : "");

/**
 * Bank transfer, client side: send a proof of payment, see whether it is under review, and send a
 * new one once a proof is rejected. Only one proof may await review at a time.
 */
export const PaymentProofUpload = ({ invoice }) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [file, setFile] = useState(null);

    const { data: proofs = [], error } = useQuery({
        queryKey: ["payment-proofs", invoice.id],
        queryFn: () => invoicesService.getPaymentProofs(invoice.id),
        retry: false,
    });

    const upload = useMutation({
        mutationFn: () => invoicesService.uploadPaymentProof(invoice.id, file),
        onSuccess: () => {
            toast.success(t("invoices.proof.uploadSuccess"));
            setFile(null);
            queryClient.invalidateQueries({ queryKey: ["payment-proofs", invoice.id] });
            queryClient.invalidateQueries({ queryKey: ["invoice"] });
            queryClient.invalidateQueries({ queryKey: ["company-invoices"] });
        },
        onError: (err) => toast.error(err?.response?.data?.message || t("invoices.proof.uploadError")),
    });

    const handleFileChange = (e) => {
        const selected = e.target.files?.[0];
        e.target.value = ""; // lets the same file be picked again after a failed upload
        if (!selected) return;
        if (!isImageOrPdf(selected)) {
            toast.error(t("invoices.proof.invalidType"));
            return;
        }
        setFile(selected);
    };

    const latest = proofs[0];
    const awaitingReview = latest?.status === "PENDING";

    return (
        <div className="p-6 border border-gray-200 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">{t("invoices.proof.title")}</h3>

            {invoice.status === "PAID" ? (
                <div className="p-4 bg-green-50 border border-green-200 rounded text-sm text-green-700">
                    <i className="fas fa-check-circle mr-2 rtl:mr-0 rtl:ml-2"></i>
                    {t("invoices.proof.paidMessage")}
                </div>
            ) : awaitingReview ? (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                    <i className="fas fa-hourglass-half mr-2 rtl:mr-0 rtl:ml-2"></i>
                    {t("invoices.proof.pendingMessage", { date: formatDate(latest.uploadedAt) })}
                </div>
            ) : (
                <>
                    {latest?.status === "REJECTED" && (
                        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                            <p>
                                <i className="fas fa-times-circle mr-2 rtl:mr-0 rtl:ml-2"></i>
                                {t("invoices.proof.rejectedMessage", { date: formatDate(latest.uploadedAt) })}
                            </p>
                            {latest.rejectionReason && (
                                <p className="mt-2">
                                    <strong>{t("invoices.proof.reasonLabel")}</strong> {latest.rejectionReason}
                                </p>
                            )}
                        </div>
                    )}

                    <p className="text-sm text-gray-600 mb-4">{t("invoices.proof.uploadHint")}</p>

                    <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                        <label className="px-4 py-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 flex items-center gap-2 min-w-0">
                            <i className="fas fa-paperclip text-gray-500"></i>
                            <span className="truncate">{file ? file.name : t("invoices.proof.chooseFile")}</span>
                            <input
                                type="file"
                                className="hidden"
                                accept="image/*,application/pdf"
                                onChange={handleFileChange}
                            />
                        </label>
                        <TaleButton
                            onClick={() => upload.mutate()}
                            loading={upload.isPending}
                            disabled={!file || upload.isPending}
                            className="bg-green-600 hover:bg-green-700 border-green-600 px-6"
                        >
                            <i className="fas fa-upload mr-2 rtl:mr-0 rtl:ml-2"></i>
                            {t("invoices.proof.submit")}
                        </TaleButton>
                    </div>
                </>
            )}

            {error && <p className="mt-4 text-sm text-red-600">{t("invoices.proof.loadError")}</p>}

            {proofs.length > 0 && (
                <div className="mt-6">
                    <h4 className="font-medium text-gray-700 mb-3">{t("invoices.proof.history")}</h4>
                    <PaymentProofHistory proofs={proofs} />
                </div>
            )}
        </div>
    );
};
